import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileSearch, PackageCheck } from 'lucide-react';
import { listCompras } from '../api/compras';
import FormField from '../components/FormField';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import { formatCNPJ, formatDateTime, formatMoney } from '../utils/formatters';

export default function ComprasPage() {
  const { emitenteId } = useEmitenteAtivo();
  const [busca, setBusca] = useState('');

  const q = useQuery({
    enabled: !!emitenteId,
    queryKey: ['compras', emitenteId, busca],
    queryFn: () => listCompras(emitenteId!, busca),
    refetchOnWindowFocus: false,
  });

  const compras = q.data?.data || [];

  if (!emitenteId) {
    return <div className="card-surface p-6 text-center text-sm text-slate-600 dark:text-slate-400">Selecione um emitente para ver compras.</div>;
  }

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="eyebrow">Entrada fiscal</div>
          <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">Compras de entrada</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">NF-e de fornecedores importadas pelo DF-e com XML completo.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/70">
          <div className="eyebrow">Total importado</div>
          <div className="mono mt-1 text-2xl text-slate-950 dark:text-slate-100">{compras.length}</div>
        </div>
        </div>
      </header>

      <section className="card-surface p-4">
        <FormField label="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Fornecedor, CNPJ ou chave" />
      </section>

      <section className="card-surface overflow-hidden">
        {q.isLoading ? (
          <div className="grid place-items-center py-12"><LoadingSpinner /></div>
        ) : compras.length === 0 ? (
          <div className="py-12 text-center text-sm text-neutral-500">
            <FileSearch className="h-8 w-8 mx-auto mb-2 text-neutral-600" />
            Nenhuma compra importada para o emitente ativo.
          </div>
        ) : (
          <>
          <ul className="space-y-2 p-3 md:hidden">
            {compras.map((c) => (
              <li key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-950 dark:text-slate-100">NF-e {c.numero}/{c.serie}</div>
                    <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{c.fornecedor_nome || '-'}</div>
                    <div className="mt-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">{c.fornecedor_doc ? formatCNPJ(c.fornecedor_doc) : '-'}</div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <PackageCheck className="h-3 w-3" />
                    {c.status}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Emissão</div>
                    <div className="text-sm font-medium">{c.data_emissao ? formatDateTime(c.data_emissao) : '-'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 dark:text-slate-400">Valor</div>
                    <div className="text-sm font-semibold tabular-nums">{formatMoney(c.valor_total || 0)}</div>
                  </div>
                </div>
                <button className="btn-secondary mt-3 w-full justify-center" onClick={() => { window.location.href = `/api/compras/${c.id}/xml?emitente_id=${emitenteId}`; }}>
                  <Download className="h-4 w-4" />
                  XML
                </button>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase tracking-[0.08em] text-slate-500 dark:bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3">Documento</th>
                  <th className="px-4 py-3">Fornecedor</th>
                  <th className="px-4 py-3">Emissão</th>
                  <th className="px-4 py-3">Valor</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {compras.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/70">
                    <td className="px-4 py-3">
                      <div className="font-medium">NF-e {c.numero}/{c.serie}</div>
                      <div className="max-w-[360px] truncate text-xs text-slate-500 dark:text-slate-400">{c.chave}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{c.fornecedor_nome || '-'}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{c.fornecedor_doc ? formatCNPJ(c.fornecedor_doc) : '-'}</div>
                    </td>
                    <td className="px-4 py-3">{c.data_emissao ? formatDateTime(c.data_emissao) : '-'}</td>
                    <td className="px-4 py-3">{formatMoney(c.valor_total || 0)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300">
                        <PackageCheck className="h-3 w-3" />
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="btn-secondary" onClick={() => { window.location.href = `/api/compras/${c.id}/xml?emitente_id=${emitenteId}`; }}>
                        <Download className="h-4 w-4" />
                        XML
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>
    </div>
  );
}
